/**
 * Loading the game's large files through the cache: `get(url, hash)` serves a file from IndexedDB
 * (store.ts) when it was kept with that hash, and otherwise downloads it (`<url>?v=<hash>`, with
 * progress) and keeps it. `getAsset(path)` and `fetchAsset(url)` look the hash up in the assets
 * manifest (manifest.ts) first; a file the manifest does not list is fetched as before.
 *
 *   const { blob, from } = await getAsset('models/hero.glb', { onProgress: (got, of) => … });
 *   const res = await fetchAsset(url);   // a drop-in for fetch(url): what render/rig/bake.ts uses
 *
 * Every download and cache hit is announced (`watchAssets`), for the loading scene (loading.ts).
 *
 * Safe to import from workers: it touches nothing until called.
 */
import { type AssetEntry, assetPath, assetsManifest, baseHref } from './manifest';
import { assetStore } from './store';

/** One file being loaded: where from, and how far it has got. */
export interface AssetTransfer {
  path: string;
  from: 'cache' | 'network';
  loaded: number;
  total: number;
  done: boolean;
}

export interface AssetLoad {
  blob: Blob;
  from: 'cache' | 'network';
}

export interface GetOptions {
  /** The file's size, when known ahead (the manifest's): for progress, and checked on download. */
  size?: number;
  onProgress?: (loaded: number, total: number) => void;
}

/** What has been loaded so far this page, by where from. */
export const assetStats = {
  cache: { files: 0, bytes: 0 },
  network: { files: 0, bytes: 0 },
};

const watchers = new Set<(t: AssetTransfer) => void>();
/** Calls `f` on every step of every file loaded through the cache; returns how to stop. */
export function watchAssets(f: (t: AssetTransfer) => void): () => void {
  watchers.add(f);
  return () => void watchers.delete(f);
}
function announce(t: AssetTransfer): void {
  for (const f of watchers) {
    try {
      f(t);
    } catch {
      // A watcher's fault is its own.
    }
  }
}

/**
 * The file downloaded is not the one the manifest lists (cut short, or another version): it is
 * neither kept nor handed over.
 */
export class AssetIntegrityError extends Error {
  constructor(
    readonly path: string,
    readonly why: string,
  ) {
    super(`${path}: ${why}`);
  }
}

/** The manifest's hashes: the first hex digits of the file's SHA-256 (scripts/lib/assets-manifest.mjs). */
const HASH_PREFIX = /^[0-9a-f]{8,64}$/;

/**
 * What is wrong with a downloaded file, or null: its size against the manifest's (when known), and
 * its SHA-256 against the manifest's hash (where the browser can work one out: not on an insecure
 * page, which has no `crypto.subtle`).
 */
export async function integrityProblem(blob: Blob, hash: string, size?: number): Promise<string | null> {
  if (size !== undefined && blob.size !== size) return `${blob.size} bytes, the manifest says ${size}`;
  const subtle = globalThis.crypto?.subtle;
  if (!subtle || !HASH_PREFIX.test(hash)) return null;
  const digest = new Uint8Array(await subtle.digest('SHA-256', await blob.arrayBuffer()));
  let hex = '';
  for (const b of digest) hex += b.toString(16).padStart(2, '0');
  return hex.startsWith(hash) ? null : `SHA-256 ${hex.slice(0, hash.length)}, the manifest says ${hash}`;
}

/** The server answered, but not with the file (a 404): handed back as the answer. */
export class AssetHttpError extends Error {
  constructor(readonly response: Response) {
    super(`HTTP ${response.status} for ${response.url}`);
  }
}

/** Marks the IndexedDB layer's own requests, so the service worker does not keep a second copy. */
const IDB_HEADER = 'x-nine-idb';

/** The body of a response, read in chunks with progress. */
async function readBody(res: Response, total: number, step: (loaded: number) => void): Promise<Blob> {
  const type = res.headers.get('content-type') ?? '';
  if (!res.body) {
    const whole = await res.blob();
    step(whole.size);
    return whole;
  }
  const reader = res.body.getReader();
  const parts: Uint8Array<ArrayBuffer>[] = [];
  let loaded = 0;
  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    parts.push(value as Uint8Array<ArrayBuffer>);
    loaded += value.byteLength;
    step(Math.min(loaded, total || loaded));
  }
  return new Blob(parts, { type });
}

const inflight = new Map<string, Promise<AssetLoad>>();

/**
 * A file by URL and content hash: from IndexedDB when kept with this hash, else downloaded and kept.
 * Throws as `fetch` does when the network fails, `AssetHttpError` when the server says no, and
 * `AssetIntegrityError` when what came is not the file the hash (and `opts.size`) name.
 */
export function get(url: string, hash: string, opts: GetOptions = {}): Promise<AssetLoad> {
  const abs = new URL(url, globalThis.document?.baseURI ?? baseHref());
  abs.search = '';
  const path = assetPath(abs.href) ?? abs.href;
  const key = `${path}#${hash}`;
  let job = inflight.get(key);
  if (!job) {
    job = load(abs, path, hash, opts).finally(() => inflight.delete(key));
    inflight.set(key, job);
  }
  return job;
}

async function load(abs: URL, path: string, hash: string, opts: GetOptions): Promise<AssetLoad> {
  const total = opts.size ?? 0;
  const kept = await assetStore.read(path, hash);
  if (kept) {
    assetStats.cache.files++;
    assetStats.cache.bytes += kept.size;
    announce({ path, from: 'cache', loaded: kept.size, total: kept.size, done: true });
    opts.onProgress?.(kept.size, kept.size);
    return { blob: kept, from: 'cache' };
  }
  const t: AssetTransfer = { path, from: 'network', loaded: 0, total, done: false };
  announce({ ...t });
  const versioned = new URL(abs);
  versioned.searchParams.set('v', hash);
  const attempt = async (cache: RequestCache): Promise<Blob> => {
    const res = await fetch(versioned, { cache, headers: { [IDB_HEADER]: '1' } });
    if (!res.ok) throw new AssetHttpError(res);
    t.total = total || Number(res.headers.get('content-length')) || 0;
    const got = await readBody(res, t.total, (loaded) => {
      t.loaded = loaded;
      announce({ ...t });
      opts.onProgress?.(loaded, t.total);
    });
    // Only the file the manifest names is kept under its hash.
    const wrong = await integrityProblem(got, hash, opts.size);
    if (wrong) throw new AssetIntegrityError(path, wrong);
    return got;
  };
  let blob: Blob;
  try {
    try {
      blob = await attempt('default');
    } catch (error) {
      // A copy cut short, or another version, from a cache on the way (the browser's, the
      // worker's), or a dropped connection: asked of the server once more before failing.
      if (error instanceof AssetHttpError) throw error;
      t.loaded = 0;
      blob = await attempt('reload');
    }
  } catch (error) {
    // Counted as nothing to wait for.
    announce({ ...t, loaded: 0, total: 0, done: true });
    throw error;
  }
  t.loaded = t.total = blob.size;
  t.done = true;
  announce({ ...t });
  opts.onProgress?.(blob.size, blob.size);
  assetStats.network.files++;
  assetStats.network.bytes += blob.size;
  // Kept in the background: the caller has its file already.
  void assetStore.write(path, hash, blob);
  return { blob, from: 'network' };
}

/** A file by its path under the base URL (`models/hero.glb`), through the cache if the manifest lists it. */
export async function getAsset(path: string, opts: Omit<GetOptions, 'size'> = {}): Promise<AssetLoad> {
  const entry = (await assetsManifest())?.files[path];
  const url = new URL(path, baseHref()).href;
  if (entry) return get(url, entry.hash, { ...opts, size: entry.size });
  const res = await fetch(url);
  if (!res.ok) throw new AssetHttpError(res);
  return { blob: await res.blob(), from: 'network' };
}

/**
 * `fetch(url)` through the cache: the same answer, from IndexedDB when the file is kept under the
 * manifest's hash. A file the manifest does not list, or any fault in the cache, is a plain fetch.
 */
export async function fetchAsset(url: string): Promise<Response> {
  let entry: AssetEntry | undefined;
  try {
    const path = assetPath(url);
    entry = path ? (await assetsManifest())?.files[path] : undefined;
  } catch {
    entry = undefined;
  }
  if (!entry) return fetch(url);
  try {
    const { blob } = await get(url, entry.hash, { size: entry.size });
    return new Response(blob, { status: 200, headers: blob.type ? { 'Content-Type': blob.type } : {} });
  } catch (error) {
    if (error instanceof AssetHttpError) return error.response;
    return fetch(url);
  }
}
